import { CurrencyCode } from '@/shared/constants';

// Only codes whose symbol differs from the default. The rest keep `$`.
// A Map, not an object literal: an object would resolve keys like `toString`
// through the prototype chain and return a function.
const CURRENCY_SYMBOLS = new Map<string, string>([
  [CurrencyCode.EUR, '€'],
  [CurrencyCode.PYG, '₲'],
]);

// Currencies with no minor unit, so they format with 0 decimals.
const ZERO_DECIMAL_CURRENCIES = new Set<string>([CurrencyCode.PYG]);

/** Returns the symbol of a currency code. Unknown codes fall back to `$`. */
export const getCurrencySymbolByCode = (currency?: string): string => {
  if (!currency) {
    return '$';
  }
  return CURRENCY_SYMBOLS.get(currency) ?? '$';
};

/** Returns how many decimals a currency shows. */
export const getCurrencyFractionDigits = (currency?: string): number => {
  if (currency && ZERO_DECIMAL_CURRENCIES.has(currency)) {
    return 0;
  }
  return 2;
};

/**
 * Resolves the number-formatting locale for a country.
 * `minimize()` can return a language with no CLDR data (PY gives `gn`), and
 * Intl then falls back to `en-US`, which formats with the wrong conventions.
 */
export const resolveCurrencyLocale = (region: string): string => {
  const minimized = new Intl.Locale('und', { region }).minimize().baseName;
  const [supported] = Intl.NumberFormat.supportedLocalesOf([
    minimized,
    `es-${region}`,
  ]);
  return supported ?? minimized;
};
